import React from 'react';
import PropTypes from 'prop-types';
import { Oval } from 'react-loader-spinner';
import { ArrowRight, Edit } from 'lucide-react';
import SubscriptionToggle from '../cart/SubscriptionToggle';
import Button from '../ui/Button';
import ProductPrice from './ProductPrice';
import './ProductSlider.scss';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { setCustomizerStep } from '../../store/customizerSlice';
import "./OnProductImageText.scss";
import "./BundleBox.scss";
import { formatDisplayPrice } from '../../utils/priceHandler';


const BundleBox = ({
  bundle,
  products,
  finalImages,
  openModal,
  handleAddToCart,
  basicProductImage = false,
  imagesLoading = false,
  className
}) => {
  const navigate = useAppNavigate();

  return (
    <div key={bundle.id} className={`product-box bundle-box flex flex-col rounded-20 p-4 box-shadow fade-step ${className}`}>
      {bundle.name &&
        <div className='flex items-start gap-4 gap-y-1 mb-2'>
          <p className='text-2xl font-bold mb-0 '>{bundle.name}</p>
          {bundle.saved > 0 && <p className='saved mb-0 whitespace-nowrap'>Save ${bundle.saved}</p>}
        </div>
      }
      {bundle.description && <p className='text-md mb-6'>{bundle.description}</p>}
      <div className='products-included grow mb-6'>
        {products.length > 0 &&
          products.map((product) => (
            <div key={product.id} className={`flex gap-4 mb-2 ${!handleAddToCart ? 'pointer-events-none' : ''}`} onClick={() => { finalImages[product.id] && openModal(product) }}>
              <div className="product-image flex items-center justify-center cursor-pointer w-[75px] h-[75px] min-w-[75px] rounded-8 overflow-hidden bg-secondary-light">
                {(imagesLoading && !finalImages[product.id]) ?
                  <Oval
                    visible={true}
                    height={30}
                    width={30}
                    color="white"
                    strokeWidth={3}
                    strokeWidthSecondary={3}
                    secondaryColor="#F6F6FC"
                    ariaLabel="oval-loading"
                  />
                  :
                  <img
                    key={finalImages[product.id] || `mockup-${product.id}`}
                    src={finalImages[product.id] || product.image}
                    alt={product.title}
                    className="w-full object-cover rounded"
                  />
                }
              </div>
              <div
                className={`cursor-pointer gap-1 ${!finalImages[product.id] ? 'pointer-events-none' : ''}`}
                onClick={() => {
                  if (basicProductImage) {
                    navigate(`/product/${product.sku}`);
                  } else if (finalImages[product.id]) {
                    openModal(product);
                  }
                }}
              >
                {product.flavor && (
                  <div className={`product-tag mb-1 capitalize ${product.category.toLowerCase()}`}>
                    {product.flavor}
                  </div>
                )}
                <p className="text-sm product-title font-bold">{product.title}</p>
              </div>
            </div>
          ))
        }
      </div>

      <div className='product-price self-end mb-2'>
        <span className="text-2xl font-medium color-primary">{formatDisplayPrice(bundle.total)}</span>
        <span className="ml-2 text-sm text-gray-500 line-through">
          {formatDisplayPrice(bundle.rawTotal)}
        </span>
      </div>

      <div>
        {!handleAddToCart ?
          <Button
            type="button"
            onClick={() => navigate('/customizer')}
            variant="filled"
            className="mt-5 justify-between"
          >
            <span>Personalize</span>
            <Edit size={20} />
          </Button>
          :
          <Button
            variant="filled"
            className="justify-between"
            type="button"
            disabled={imagesLoading && !products.every(product => finalImages[product.id])}
            onClick={() =>
              bundle.products.forEach((product) => {
                handleAddToCart(product, false, 1, bundle.id, bundle.name, bundle.total, bundle.rawTotal, bundle.discountAmount);
              })
            }
          >
            Add to Cart
            <ArrowRight size={27} />
          </Button>
        }
      </div>
    </div>
  );
};

BundleBox.propTypes = {
  bundle: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    saved: PropTypes.number,
    total: PropTypes.number.isRequired,
    rawTotal: PropTypes.number.isRequired
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    flavor: PropTypes.string,
    image: PropTypes.string,
    sku: PropTypes.string,
    category: PropTypes.string
  })).isRequired,
  finalImages: PropTypes.object,
  openModal: PropTypes.func,
  handleAddToCart: PropTypes.func,
  basicProductImage: PropTypes.bool,
  imagesLoading: PropTypes.bool,
  className: PropTypes.string
};

export default BundleBox;
